import { useRef, useState } from 'react';

import { useLocation } from 'react-router';

import {
  useCurrentUser,
  useLegalConfig,
  useNeedsConsent,
  useUpdateConsent,
  useUserDocument,
} from '@chartai/api-client';

import { PrivacyContent } from '@/shared/components/legal/PrivacyContent';
import { TermsContent } from '@/shared/components/legal/TermsContent';
import { Button } from '@/shared/components/ui/Button';
import { Checkbox } from '@/shared/components/ui/Checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog';

// CAB-505: the consent gate must outrank the profile lock and billing, so the
// ONLY routes it exempts are the public /legal pages (where the full text is
// shown without a covering modal). The previous list exempted
// '/dashboard/profile' (which startsWith-matched '/dashboard/profiles', the
// ProfileGuard redirect target) and '/subscriptions', which inverted the
// intended priority.
const EXEMPT_PATH_PREFIXES = ['/legal'];

function isExemptPath(pathname: string): boolean {
  return EXEMPT_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/** Px tolerance for the scroll-to-bottom latch — accounts for fractional
 *  scroll heights and sub-pixel rounding across browsers. */
const SCROLL_BOTTOM_EPSILON = 24;

export function LegalConsentModal() {
  const [accepted, setAccepted] = useState(false);
  // CAB-505: the accept control stays disabled until the user has scrolled to
  // the bottom of the combined legal text. Starts true only when the content
  // fits without scrolling (short content needs no scroll).
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const { user } = useCurrentUser();
  const { data: userDoc } = useUserDocument(user?.uid ?? null);
  const { data: legalConfig } = useLegalConfig();
  const needsConsent = useNeedsConsent(userDoc ?? null, legalConfig ?? null);
  const { mutate: updateConsent, isPending } = useUpdateConsent();

  if (!needsConsent || isExemptPath(location.pathname)) {
    return null;
  }

  if (!user || !legalConfig) {
    return null;
  }

  // CAB-516: the same gate serves brand-new signups (first acceptance) and
  // existing users re-consenting after a version bump. For a first-timer there
  // is nothing "updated", so the wording must not say so. We treat the user as
  // re-consenting only if they previously accepted SOME version of either
  // document; absent any prior acceptance it is a first acceptance.
  const hasPriorAcceptance =
    userDoc?.['termsAccepted'] === true ||
    userDoc?.['privacyAccepted'] === true ||
    (typeof userDoc?.['termsAcceptedVersion'] === 'string' &&
      userDoc['termsAcceptedVersion'] !== '') ||
    (typeof userDoc?.['privacyAcceptedVersion'] === 'string' &&
      userDoc['privacyAcceptedVersion'] !== '');
  const isUpdate = hasPriorAcceptance;

  // When the scroll container mounts, enable acceptance immediately if the
  // content already fits (nothing to scroll). Otherwise the scroll handler
  // latches `scrolledToBottom` once the user reaches the end.
  const handleScrollRef = (node: HTMLDivElement | null) => {
    scrollRef.current = node;
    // Only auto-enable when the container has actually laid out (positive
    // height) AND the content fits without scrolling. A not-yet-measured
    // container reports 0/0 — treat that as "scroll still required" so the
    // gate never unlocks before the user has seen the text.
    if (
      node &&
      node.clientHeight > 0 &&
      node.scrollHeight - node.clientHeight <= SCROLL_BOTTOM_EPSILON
    ) {
      setScrolledToBottom(true);
    }
  };

  const handleScroll = () => {
    const node = scrollRef.current;
    if (!node || node.clientHeight <= 0) {
      return;
    }
    if (node.scrollHeight - node.scrollTop - node.clientHeight <= SCROLL_BOTTOM_EPSILON) {
      setScrolledToBottom(true);
    }
  };

  const handleAccept = () => {
    const termsVersion = legalConfig['termsVersion'];
    const privacyVersion = legalConfig['privacyVersion'];
    updateConsent({
      uid: user.uid,
      termsAcceptedVersion: typeof termsVersion === 'string' ? termsVersion : '',
      privacyAcceptedVersion: typeof privacyVersion === 'string' ? privacyVersion : '',
      acceptedPlatform: 'web',
    });
  };

  return (
    // CAB-505: always non-dismissable on authenticated routes — no Escape,
    // outside-click, or close button can dismiss the gate.
    <Dialog open onOpenChange={() => undefined}>
      <DialogContent
        onPointerDownOutside={(e) => {
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
        className='max-w-2xl [&>button[aria-label="Close"]]:hidden'
      >
        <DialogHeader>
          <DialogTitle>{isUpdate ? 'Updated Terms' : 'Terms'}</DialogTitle>
          <DialogDescription>
            {isUpdate
              ? 'Our Terms of Service and Privacy Policy have been updated. Please review the full text below and accept to continue using ChartAI.'
              : 'Please review our Terms of Service and Privacy Policy below and accept to continue using ChartAI.'}
          </DialogDescription>
        </DialogHeader>

        <div
          ref={handleScrollRef}
          onScroll={handleScroll}
          data-testid="legal-consent-scroll"
          className="border-border max-h-[60vh] overflow-y-auto rounded-lg border p-4"
        >
          <TermsContent />
          <hr className="border-border my-8" />
          <PrivacyContent />
        </div>

        <div className="bg-muted flex items-center gap-3 rounded-lg px-4 py-3">
          <Checkbox
            id="legal-consent"
            checked={accepted}
            disabled={!scrolledToBottom}
            onChange={(e) => {
              setAccepted(e.target.checked);
            }}
          />
          <label htmlFor="legal-consent" className="text-foreground text-sm">
            {scrolledToBottom
              ? isUpdate
                ? 'I accept the updated Terms and Privacy Policy'
                : 'I accept the Terms and Privacy Policy'
              : 'Scroll to the bottom to accept the Terms and Privacy Policy'}
          </label>
        </div>

        <DialogFooter>
          <Button onClick={handleAccept} disabled={!accepted || !scrolledToBottom || isPending}>
            {isPending ? 'Saving...' : 'Accept & Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
